import { Component, computed, effect, inject, signal, viewChild } from '@angular/core';
import { CommonModule, formatNumber } from '@angular/common';
import { Router } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ChartComponent } from 'ng-apexcharts';
import { BaseChartOptions, DEFAULT_CHART_OPTIONS } from 'shared/types/chart.config';
import { TabsV2Component } from 'shared/components/tabs-v2/tabs-v2.component';
import { KorviuListComponent } from 'shared/components/korviu-list/korviu-list.component';
import { KorviuButtonComponent } from 'shared/components/korviu-button/korviu-button.component';
import { EmptyTemplateDirective, KorviuNoDataComponent } from 'shared';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { IKorviuListConfig, KorviuListItemTypes } from 'shared/types/korviu-list.config';
import { SortOrders } from 'shared/types/sort-order.enum';
import { BalanceService, IEarningsMonth } from 'core/services/balance.service';
import { KorviuDatePickerComponent } from 'shared/components/korviu-date-picker/korviu-date-picker.component';
import { ISortSection } from 'shared/types/sort-section.interface';
import { IFilterSection } from 'shared/types/filter-section.interface';
import { IPagination } from 'shared/types/pagination.interface';
import { IFilter } from 'shared/types/filter.interface';
import { ISorting } from 'shared/types/sorting.interface';
import { catchError, map, merge, Observable, of, startWith, tap } from 'rxjs';
import { IGetPaginatedResponse } from 'shared/types/get-paginated-response.interface';
import { FormControl } from '@angular/forms';
import { ContactUsModalComponent } from '../../../shared/modal/contact-us-modal/contact-us-modal.component';
import { NzModalService } from 'ng-zorro-antd/modal';
import { ITransaction } from 'core/types/transaction.interface';
import { TagComponent, TagComponentType } from 'shared/components/tag/tag.component';
import { format } from 'date-fns';
import { enUS, es } from 'date-fns/locale';
import {
  DATE_HELPER_SERVICE_FACTORY,
  DateHelperService,
  en_US,
  es_ES,
  NZ_DATE_LOCALE,
  NzI18nService
} from 'ng-zorro-antd/i18n';
import {
  formatBalanceDate,
  formatBalanceMoney,
  formatBalanceTransactionDate,
  parseBalanceApiDate
} from 'shared/utils/balance-format.utils';
import { transactionTypeFilters } from 'shared/utils/balance-transaction-filters';

enum PaymentTab {
  PAYMENTS,
  TRANSACTIONS
}

export function formatNextPaymentPeriod(value: string | undefined, locale: string): string {
  return formatBalanceDate(value, locale, 'period');
}

export function formatNextPaymentDueDate(value: string | undefined, locale: string): string {
  return formatBalanceDate(value, locale);
}

@Component({
  selector: 'app-balance-special-users',
  standalone: true,
  imports: [
    CommonModule,
    ChartComponent,
    TabsV2Component,
    KorviuListComponent,
    KorviuButtonComponent,
    TranslatePipe,
    KorviuDatePickerComponent,
    EmptyTemplateDirective,
    KorviuNoDataComponent,
    TagComponent
  ],
  providers: [
    NzI18nService,
    { provide: NZ_DATE_LOCALE, useValue: enUS },
    { provide: DateHelperService, useFactory: DATE_HELPER_SERVICE_FACTORY }
  ],
  templateUrl: './balance-special-users.component.html',
  styleUrl: './balance-special-users.component.scss'
})
export class BalanceSpecialUsersComponent {
  readonly transactionList = viewChild('transactionList', { read: KorviuListComponent });
  readonly endDatePicker = viewChild('endDatePicker', { read: KorviuDatePickerComponent });

  readonly #balanceService = inject(BalanceService);
  readonly #translateService = inject(TranslateService);
  readonly #modalService = inject(NzModalService);
  readonly #router = inject(Router);
  readonly #dateI18n = inject(NzI18nService);

  readonly language = toSignal(
    this.#translateService.onLangChange.pipe(
      map(event => event.lang),
      startWith(this.#translateService.currentLang || this.#translateService.defaultLang || 'es')
    ),
    { requireSync: true }
  );

  readonly startDateControl = new FormControl<Date | null>(null);
  readonly endDateControl = new FormControl<Date | null>(null);

  readonly summaryCardsDataResource = this.#balanceService.getBalanceSummaryCards();
  readonly summaryCardsData = computed(() =>
    this.summaryCardsDataResource.hasValue() ? this.summaryCardsDataResource.value() : undefined
  );
  readonly summaryCardsError = computed(() => this.summaryCardsDataResource.status() === 'error');
  readonly summaryCardsDisplay = computed(() => {
    const data = this.summaryCardsData();

    if (!data) {
      return null;
    }

    const locale = this.language();
    const promotionFunds = Number(data.promotion_funds ?? 0);

    return {
      commission_monthly: formatBalanceMoney(data.commission_monthly ?? 0),
      pending_payments: formatBalanceMoney(data.pending_payments ?? 0),
      paid_earnings: formatBalanceMoney(data.paid_earnings ?? 0),
      promotion_funds: Number.isFinite(promotionFunds) ? formatNumber(Math.floor(promotionFunds), locale, '1.0-0') : '—'
    };
  });

  readonly currentPayment = toSignal(this.#balanceService.getCurrentPaymentDetails().pipe(catchError(() => of(null))), {
    initialValue: null
  });
  readonly currentPaymentMonthLabel = computed(() => {
    const date = parseBalanceApiDate(this.currentPayment()?.period);

    return date ? new Intl.DateTimeFormat(this.language(), { month: 'long', timeZone: 'UTC' }).format(date) : '';
  });

  readonly currentMonthCommissions = computed(() => {
    const date = parseBalanceApiDate(this.currentPayment()?.period) ?? new Date();
    const month = new Intl.DateTimeFormat(this.language(), { month: 'long', timeZone: 'UTC' }).format(date);
    return this.#translateService.instant('balance.monthCommissions', { month });
  });

  readonly nextPaymentResource = this.#balanceService.getNextPayment();
  readonly nextPayment = computed(() =>
    this.nextPaymentResource.hasValue() ? this.nextPaymentResource.value() : undefined
  );
  readonly nextPaymentError = computed(() => this.nextPaymentResource.status() === 'error');
  readonly nextPaymentAmount = computed(() => this.nextPayment()?.next_payment_amount);
  readonly nextPaymentAmountLabel = computed(() => formatBalanceMoney(this.nextPaymentAmount()));
  readonly nextPaymentPeriodLabel = computed(() =>
    formatNextPaymentPeriod(this.nextPayment()?.payment_period, this.language())
  );
  readonly nextPaymentDueDateLabel = computed(() =>
    formatNextPaymentDueDate(this.nextPayment()?.payment_due_date, this.language())
  );
  readonly nextPaymentStatus = computed(() => this.nextPayment()?.payment_status);
  readonly nextPaymentStatusTranslationKey = computed(() => {
    const status = this.nextPaymentStatus();
    return status ? `balance.${status.toLowerCase()}` : null;
  });
  readonly nextPaymentStatusTagType = computed<TagComponentType>(() =>
    this.nextPaymentStatus() === 'Overdue' ? 'error' : 'warning'
  );
  readonly nextPaymentLoading = computed(() => this.nextPaymentResource.isLoading());

  readonly earningsGrowthGraphResource = this.#balanceService.getEarningsGrowthGraph();
  readonly earningsGrowthGraphData = computed(() =>
    this.earningsGrowthGraphResource.hasValue() ? this.earningsGrowthGraphResource.value() : undefined
  );
  readonly earningsGrowthGraphError = computed(() => this.earningsGrowthGraphResource.status() === 'error');
  readonly earningsGrowthGraphLoading = computed(() => this.earningsGrowthGraphResource.isLoading());

  readonly earningsGrowthMonths = computed<IEarningsMonth[]>(() => this.earningsGrowthGraphData()?.months || []);
  readonly hasEarningsGrowthData = computed(() => this.earningsGrowthMonths().length > 0);
  readonly earningsTrendPercentage = computed<number | null>(() => {
    const percentage = Number(this.earningsGrowthGraphData()?.trend_percentage);

    return Number.isFinite(percentage) ? Math.round(Math.abs(percentage) * 10) / 10 : null;
  });
  readonly earningsTrendIsUp = computed(() => this.earningsGrowthGraphData()?.trend_direction === 'up');
  // Neutral periods and a missing percentage have nothing to report, so the trend line is dropped instead of
  // showing a direction the API did not confirm.
  readonly earningsTrendTranslationKey = computed<string | null>(() => {
    const direction = this.earningsGrowthGraphData()?.trend_direction;

    if (!this.earningsTrendPercentage() || (direction !== 'up' && direction !== 'down')) {
      return null;
    }

    return direction === 'up' ? 'balance.trendingUpOverPriorPeriod' : 'balance.trendingDownOverPriorPeriod';
  });

  public balanceSortSections: ISortSection[] = [
    {
      localizableName: 'common.sortOption.date',
      options: [
        {
          localizableName: 'common.sortOption.newest',
          sortDirection: { sortBy: 'timestamp', sortOrder: SortOrders.DESCEND }
        },
        {
          localizableName: 'common.sortOption.oldest',
          sortDirection: { sortBy: 'timestamp', sortOrder: SortOrders.ASCEND }
        }
      ]
    },
    {
      localizableName: 'common.sortOption.ganasAmount',
      options: [
        {
          localizableName: 'common.sortOption.highest',
          sortDirection: { sortBy: 'amount', sortOrder: SortOrders.DESCEND }
        },
        {
          localizableName: 'common.sortOption.lowest',
          sortDirection: { sortBy: 'amount', sortOrder: SortOrders.ASCEND }
        }
      ]
    },
    {
      localizableName: 'common.sortOption.name',
      options: [
        {
          localizableName: 'common.sortOption.A-Z',
          sortDirection: { sortBy: 'name', sortOrder: SortOrders.ASCEND }
        },
        {
          localizableName: 'common.sortOption.Z-A',
          sortDirection: { sortBy: 'name', sortOrder: SortOrders.DESCEND }
        }
      ]
    }
  ];
  public balanceFilterSections: IFilterSection[] = [
    {
      name: this.#translateService.instant('common.sortOption.type'),
      localizableName: 'common.sortOption.type',
      parameter_name: 'status',
      checkboxModel: [
        {
          label: this.#translateService.instant('common.sortOption.deposit'),
          localizableName: 'common.sortOption.deposit',
          value: 'Deposit',
          parameter_name: 'Deposit'
        },
        {
          label: this.#translateService.instant('common.sortOption.pending'),
          localizableName: 'common.sortOption.failedTransaction',
          value: 'Failed',
          parameter_name: 'Failed'
        },
        {
          label: this.#translateService.instant('common.sortOption.pending'),
          localizableName: 'common.sortOption.pending',
          value: 'Pending',
          parameter_name: 'Pending'
        },
        {
          label: this.#translateService.instant('common.sortOption.redeemed'),
          localizableName: 'common.sortOption.redeemed',
          value: 'Redeemed',
          parameter_name: 'Redeemed'
        },
        {
          label: this.#translateService.instant('common.sortOption.purchases'),
          localizableName: 'common.sortOption.purchases',
          value: 'Purchases',
          parameter_name: 'Purchases'
        },
        {
          label: this.#translateService.instant('common.sortOption.shared'),
          localizableName: 'common.sortOption.shared',
          value: 'Shared',
          parameter_name: 'Shared'
        }
      ]
    }
  ];

  public transactionListConfig: IKorviuListConfig = {
    getFn: this._getTransactionList.bind(this),
    hideSearch: true,
    filterSections: this.balanceFilterSections,
    sortSections: this.balanceSortSections,
    itemType: KorviuListItemTypes.TRANSACTION,
    totalLocalizableName: 'balance.transactions'
  };

  korviuListConfig: IKorviuListConfig = {
    totalLocalizableName: 'balance.payments',
    getFn: this.#balanceService.getPayments.bind(this.#balanceService),
    itemType: KorviuListItemTypes.SPECIAL_PAYMENTS,
    hideSearch: true,
    sortSections: [
      {
        localizableName: 'balance.paymentDate',
        options: [
          {
            localizableName: 'common.sortOption.newest',
            sortDirection: { sortBy: 'payment_date', sortOrder: SortOrders.DESCEND }
          },
          {
            localizableName: 'common.sortOption.oldest',
            sortDirection: { sortBy: 'payment_date', sortOrder: SortOrders.ASCEND }
          }
        ]
      },
      {
        localizableName: 'balance.period',
        options: [
          {
            localizableName: 'common.sortOption.newest',
            sortDirection: { sortBy: 'period', sortOrder: SortOrders.DESCEND }
          },
          {
            localizableName: 'common.sortOption.oldest',
            sortDirection: { sortBy: 'period', sortOrder: SortOrders.ASCEND }
          }
        ]
      },
      {
        localizableName: 'balance.amount',
        options: [
          {
            localizableName: 'common.sortOption.highest',
            sortDirection: { sortBy: 'amount', sortOrder: SortOrders.DESCEND }
          },
          {
            localizableName: 'common.sortOption.lowest',
            sortDirection: { sortBy: 'amount', sortOrder: SortOrders.ASCEND }
          }
        ]
      }
    ],
    filterSections: [
      {
        name: '',
        localizableName: 'balance.status',
        parameter_name: 'status',
        checkboxModel: [
          {
            parameter_name: 'Paid',
            localizableName: 'balance.paid',
            value: 'Paid',
            label: this.#translateService.instant('balance.paid')
          },
          {
            parameter_name: 'Pending',
            localizableName: 'balance.pending',
            value: 'Pending',
            label: this.#translateService.instant('balance.pending')
          },
          {
            parameter_name: 'Overdue',
            localizableName: 'balance.overdue',
            value: 'Overdue',
            label: this.#translateService.instant('balance.overdue')
          },
          {
            parameter_name: 'Failed',
            localizableName: 'balance.failed',
            value: 'Failed',
            label: this.#translateService.instant('balance.failed')
          },
          // BAK-650: a withheld payable month and a month a carried deficit absorbed.
          {
            parameter_name: 'Held',
            localizableName: 'balance.held',
            value: 'Held',
            label: this.#translateService.instant('balance.held')
          },
          {
            parameter_name: 'Carried',
            localizableName: 'balance.carried',
            value: 'Carried',
            label: this.#translateService.instant('balance.carried')
          },
          {
            parameter_name: 'Adjustment',
            localizableName: 'balance.adjustment',
            value: 'Adjustment',
            label: this.#translateService.instant('balance.adjustment')
          }
        ]
      }
    ]
  };

  selectedPaymentTab = signal(PaymentTab.PAYMENTS);
  readonly transactionEmptyMessageKey = signal('balance.youHaventMadeAnyTransactionsYet');
  PaymentTab = PaymentTab;

  paymentTabs = computed(() => [
    { id: PaymentTab.PAYMENTS, label: 'balance.payments' },
    { id: PaymentTab.TRANSACTIONS, label: 'balance.transactions' }
  ]);

  // График роста доходов
  earningsGrowthChartOptions = computed<BaseChartOptions>(() => {
    const locale = this.language();
    const months = this.earningsGrowthMonths();
    const categories = months.map(
      month =>
        formatBalanceDate(`${month.year}-${String(month.month_number).padStart(2, '0')}-01`, locale, 'month') ||
        month.month
    );
    const seriesData = months.map(m => m.earnings);

    return {
      ...DEFAULT_CHART_OPTIONS,
      chart: {
        ...DEFAULT_CHART_OPTIONS.chart,
        type: 'bar',
        height: 210,
        width: '100%',
        toolbar: { show: false }
      },

      series: [
        {
          name: this.#translateService.instant('balance.earningsGrowth'),
          data: seriesData
        }
      ],

      colors: ['#007DCB'],

      xaxis: {
        ...DEFAULT_CHART_OPTIONS.xaxis,
        categories,
        labels: {
          show: true,
          style: {
            fontSize: '1.125rem',
            colors: '#737980',
            fontFamily: 'Outfit',
            fontWeight: '400'
          }
        },
        axisBorder: { show: false },
        axisTicks: { show: false }
      },

      yaxis: {
        ...DEFAULT_CHART_OPTIONS.yaxis,
        labels: { show: false },
        axisBorder: { show: false },
        axisTicks: { show: false }
      },

      plotOptions: {
        ...DEFAULT_CHART_OPTIONS.plotOptions,
        bar: {
          ...DEFAULT_CHART_OPTIONS.plotOptions.bar,
          horizontal: false,
          borderRadius: 4,
          columnWidth: '70%', // Процентное значение от доступного пространства для каждого столбца
          dataLabels: {
            position: 'top'
          }
        }
      },

      dataLabels: {
        ...DEFAULT_CHART_OPTIONS.dataLabels,
        enabled: true,
        offsetY: -10,
        formatter: (val: number | string) => formatBalanceMoney(val),
        style: {
          fontSize: '1.125rem',
          fontFamily: 'Outfit',
          fontWeight: '400',
          colors: ['#505760']
        }
      },

      grid: {
        show: false
      },

      tooltip: {
        enabled: false
      },

      responsive: [
        {
          breakpoint: 768,
          options: {
            dataLabels: { enabled: false },
            tooltip: {
              enabled: true,
              shared: true,
              intersect: false,
              hideEmptySeries: false,
              marker: { show: false },
              y: {
                formatter: (value: number) => formatBalanceMoney(value),
                title: { formatter: () => '' }
              }
            }
          }
        }
      ],

      legend: {
        show: false
      },

      states: {
        hover: {
          filter: { type: 'none' }
        },
        active: {
          allowMultipleDataPointsSelection: false,
          filter: { type: 'none' }
        }
      }
    };
  });

  constructor() {
    effect(() => {
      const spanish = this.language().toLowerCase().startsWith('es');
      this.#dateI18n.setDateLocale(spanish ? es : enUS);
      this.#dateI18n.setLocale(spanish ? es_ES : en_US);
    });
    this.#translateService.onLangChange
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.transactionList()?.reloadData());

    merge(this.startDateControl.valueChanges, this.endDateControl.valueChanges)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.transactionList()?.reloadData(true));
  }

  public onContactUsClick(): void {
    this.#modalService.create({
      nzContent: ContactUsModalComponent,
      nzCentered: true,
      nzClosable: false,
      nzWidth: 554,
      nzWrapClassName: 'modal-wrap'
    });
  }

  public onStartDateSelected(): void {
    if (!this.endDateControl.value) this.endDatePicker()?.openCalendar();
  }

  public onViewMonthCommissionsClick(): void {
    void this.#router.navigate(['/workspace/balance/payments', 'current']);
  }

  private _getTransactionList(
    pagination: IPagination,
    filters: IFilter[],
    sorting: ISorting
  ): Observable<IGetPaginatedResponse<ITransaction[]>> {
    const dateFilters = [...filters];
    const startDate = this.startDateControl.value;
    const endDate = this.endDateControl.value;

    if (startDate) {
      dateFilters.push({ name: 'start_date', value: format(startDate, 'yyyy-MM-dd') });
    }

    if (endDate) {
      dateFilters.push({ name: 'end_date', value: format(endDate, 'yyyy-MM-dd') });
    }

    const normalizedSorting =
      sorting?.sortBy === 'name'
        ? {
            ...sorting,
            sortBy: `${sorting.sortBy}_${this.#translateService?.currentLang}`
          }
        : sorting;

    return this.#balanceService
      .getBalanceTransactions(pagination, transactionTypeFilters(dateFilters), normalizedSorting)
      .pipe(
        tap(() =>
          this.transactionEmptyMessageKey.set(
            dateFilters.length ? 'balance.noTransactionsMatchFilters' : 'balance.youHaventMadeAnyTransactionsYet'
          )
        ),
        map(response => ({
          ...response,
          results: response.results.map(transaction => ({
            ...transaction,
            amount_display: formatBalanceMoney(transaction.amount),
            timestamp_display: formatBalanceTransactionDate(
              transaction.timestamp_display,
              transaction.event_date || transaction.timestamp,
              this.language()
            )
          }))
        }))
      );
  }
}
